/**
 * Create World Band
 *
 * The bottom-of-page creation funnel. STRATEGIC HEADLINE: most users won't know
 * how to use an SDK, so this band leads with a NO-CODE, in-app path for non-
 * technical GMs/players and demotes the SDK to a secondary one-liner for devs.
 *
 * Gold economy: the ONLY gold fill in this band is the "Create a World" primary
 * CTA. The SDK link and everything else stay silver/achromatic so the no-code
 * path never has to compete with the developer path.
 */

import { GouldCorner, KnotworkBorder } from '../Ornaments';
import { Icon } from '../Icon';

const GOLD = '#d4af37';
const GOLD_DIM = 'rgba(212, 175, 55, 0.3)';
const SILVER = '#c0c0c0';
const SILVER_DIM = 'rgba(192, 192, 192, 0.35)';

interface CreateWorldBandProps {
  /** Open a fresh CampaignWizard (no playset) — the no-code "Create a World" path. */
  onCreateWorld: () => void;
  /**
   * Pre-login front door: relabel the single gold CTA as a signup invite, since
   * acting hands off to signup rather than creating now. Still the only gold fill
   * in the band — preserves the gold economy.
   */
  loggedOut?: boolean;
}

export function CreateWorldBand({ onCreateWorld, loggedOut = false }: CreateWorldBandProps) {
  return (
    <section style={styles.band}>
      {/* Ornamental corners */}
      <GouldCorner size={28} color={GOLD_DIM} style={{ position: 'absolute', top: 10, left: 10 }} />
      <GouldCorner size={28} color={GOLD_DIM} style={{ position: 'absolute', top: 10, right: 10, transform: 'scaleX(-1)' }} />
      <GouldCorner size={28} color={GOLD_DIM} style={{ position: 'absolute', bottom: 10, left: 10, transform: 'scaleY(-1)' }} />
      <GouldCorner size={28} color={GOLD_DIM} style={{ position: 'absolute', bottom: 10, right: 10, transform: 'scale(-1, -1)' }} />

      {/* Faint knotwork glow behind the text */}
      <div style={styles.knotwork}>
        <KnotworkBorder size={360} style={{ opacity: 0.12 }} />
      </div>

      <div style={styles.inner}>
        {/* PRIMARY — the no-code path, for everyone. */}
        <div style={styles.kicker}>
          <Icon name="sparkle" size={14} color={GOLD} />
          No code required
        </div>

        <h2 style={styles.title}>Create Your Own World</h2>

        <p style={styles.copy}>
          Describe your setting and Machina builds it for you — maps, NPCs, factions, and lore,
          generated as you go. No code, no spreadsheets.
        </p>

        <div style={styles.actions}>
          <button onClick={onCreateWorld} style={styles.primaryBtn}>
            <Icon name="sparkle" size={16} />
            {loggedOut ? 'Sign up to create a World' : 'Create a New World'}
          </button>
        </div>

        {/* SECONDARY — the developer path. Muted/silver, never competes with gold. */}
        <div style={styles.devLine}>
          <span style={styles.devText}>
            Developer?{' '}
            <a href="/sdk" target="_blank" rel="noopener noreferrer" style={styles.devLink}>
              Build and share installable modules with the Machina SDK →
            </a>
          </span>
          <code style={styles.cliPill}>npx machina init</code>
        </div>
      </div>
    </section>
  );
}

const styles: Record<string, React.CSSProperties> = {
  band: {
    position: 'relative',
    // The parent (WorldsPage <main>) is a column flex container. Pin the band so
    // it sizes to its content rather than being squeezed by the flex algorithm.
    flexShrink: 0,
    width: '100%',
    maxWidth: 1200,
    margin: 'var(--spacing-xl) auto 0',
    padding: 'var(--spacing-xl)',
    backgroundColor: 'var(--color-surface)',
    background: 'radial-gradient(circle at 50% 0%, rgba(212, 175, 55, 0.06), transparent 60%), var(--color-surface)',
    border: `2px solid ${GOLD_DIM}`,
    borderRadius: 'var(--radius-md)',
    boxShadow: '0 8px 40px rgba(0, 0, 0, 0.4), inset 0 0 60px rgba(212, 175, 55, 0.03)',
    overflow: 'hidden',
  },

  knotwork: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    pointerEvents: 'none',
  },

  inner: {
    position: 'relative',
    textAlign: 'center',
    maxWidth: 760,
    margin: '0 auto',
  },

  kicker: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    fontSize: 'var(--font-size-xs)',
    color: GOLD,
    textTransform: 'uppercase',
    letterSpacing: '0.15em',
    marginBottom: 'var(--spacing-md)',
  },

  title: {
    margin: 0,
    fontSize: '2rem',
    fontFamily: 'var(--font-display)',
    fontWeight: 400,
    color: 'var(--color-text)',
    letterSpacing: '0.05em',
    textShadow: '0 2px 12px rgba(212, 175, 55, 0.2)',
  },

  copy: {
    margin: 'var(--spacing-md) auto var(--spacing-xl)',
    maxWidth: 620,
    fontSize: 'var(--font-size-md)',
    color: 'var(--color-text-secondary)',
    lineHeight: 1.6,
  },

  actions: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 'var(--spacing-md)',
  },

  // The only gold fill in this band.
  primaryBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 'var(--spacing-sm)',
    padding: 'var(--spacing-sm) var(--spacing-xl)',
    background: `linear-gradient(180deg, ${GOLD} 0%, #a08030 50%, #8b6914 100%)`,
    border: `2px solid ${GOLD}`,
    borderRadius: 'var(--radius-sm)',
    color: '#1a1410',
    fontSize: 'var(--font-size-md)',
    fontFamily: 'var(--font-display)',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    cursor: 'pointer',
    boxShadow: '0 2px 10px rgba(212, 175, 55, 0.3)',
  },

  // Secondary developer sub-line. Achromatic — silver text + a de-emphasized
  // CLI pill. Deliberately quiet so the SDK doesn't visually compete with gold.
  devLine: {
    marginTop: 'var(--spacing-xl)',
    paddingTop: 'var(--spacing-lg)',
    borderTop: `1px solid ${SILVER_DIM}`,
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 'var(--spacing-sm)',
  },

  devText: {
    fontSize: 'var(--font-size-sm)',
    color: 'var(--color-text-muted)',
    lineHeight: 1.5,
  },

  devLink: {
    color: SILVER,
    textDecoration: 'none',
    borderBottom: `1px solid ${SILVER_DIM}`,
  },

  cliPill: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '2px 10px',
    backgroundColor: 'var(--color-background)',
    border: `1px solid ${SILVER_DIM}`,
    borderRadius: 'var(--radius-sm)',
    color: 'var(--color-text-muted)',
    fontSize: 'var(--font-size-xs)',
    fontFamily: 'monospace',
    letterSpacing: '0.02em',
  },
};
